import { Box, Flex } from '@chakra-ui/react';
import { DateInputComp } from 'components/Views/components/DateInputComp';
import { addDays, addYears, format, parseISO, subYears } from 'date-fns';
import { useDispatch } from 'react-redux';
import {
  REVIEW_FORM_STAGE,
  setReviewCreationKey,
  setReviewPeriodDetails,
  useReviewCreationV1Data,
} from 'reducers/reviewCreationV1Slice';
import { getDateWithEndOfDay, getDateWithStartOfDay } from 'utils/helpers/dateHelpers';

import { SettingsV1 } from 'assets/assetsV1/iconComponents';

import { Button } from 'uiToolkitV1/Button';
import { DatePicker } from 'uiToolkitV1/DatePicker';
import { NumberInput } from 'uiToolkitV1/InputV2/NumberInput';
import { TooltipInfoV1 } from 'uiToolkitV1/TooltipInfoV1';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { LabelFieldWrapper } from '../components/LabelFieldWrapper';
import { RestrictAccessContainer, SectionWrapper } from '../components/SectionWrapper';
import { useUpdateReviewPeriod } from '../dataHooks/timelineAndRemindersSectionHooks';
import { REVIEW_CREATION_TYPES } from './NameAndReviewees';
import { ContinuousTimeline } from './timelineAndRemindersComps/ContinuousTimeline';
import { OneTimeTimeline } from './timelineAndRemindersComps/OneTimeTimeline';

const MAX_CONTINUOUS_REVIEW_END_OFFSET = 365;
const MAX_CONTINUOUS_REVIEW_START_OFFSET = 365;
const MIN_CONTINUOUS_REVIEW_START_OFFSET = 0;

const OneTimeDatesSelector = () => {
  const { reviewPeriodStart, reviewPeriodEnd } = useReviewCreationV1Data();
  const dispatch = useDispatch();
  const { mutate: updateReviewPeriod } = useUpdateReviewPeriod();

  const startDateObj = reviewPeriodStart ? getDateWithStartOfDay(parseISO(reviewPeriodStart)) : null;
  const endDateObj = reviewPeriodEnd ? getDateWithEndOfDay(parseISO(reviewPeriodEnd)) : null;

  const minEndDate = reviewPeriodStart ? addDays(startDateObj, 1) : subYears(getDateWithEndOfDay(), 5);
  const maxStartDate = reviewPeriodEnd ? addDays(endDateObj, -1) : addYears(getDateWithStartOfDay(), 5);
  const minStartDate = subYears(getDateWithStartOfDay(), 5);
  const maxEndDate = addYears(getDateWithEndOfDay(), 5);

  const onChange = (date, isStartDate) => {
    const simpleDate = format(date, 'yyyy-MM-dd');
    if (isStartDate) {
      dispatch(setReviewCreationKey({ reviewPeriodStart: simpleDate }));
      updateReviewPeriod({ changedPropertyKey: 'reviewPeriodStart', changedPropertyValue: simpleDate });
    } else {
      dispatch(setReviewCreationKey({ reviewPeriodEnd: simpleDate }));
      updateReviewPeriod({ changedPropertyKey: 'reviewPeriodEnd', changedPropertyValue: simpleDate });
    }
  };

  return (
    <Box gridColumn="2 / span 2">
      <Flex gap={3} w="600px" alignItems="center">
        Period starts from
        <DatePicker
          CustomInput={DateInputComp}
          selectedDate={startDateObj}
          onChange={(val) => onChange(val, true)}
          customInputProps={{ width: '100%' }}
          maxDate={maxStartDate}
          minDate={minStartDate}
          popperPlacement="right-start"
        />
        <TextBodyMedium>to</TextBodyMedium>
        <DatePicker
          CustomInput={DateInputComp}
          selectedDate={endDateObj}
          onChange={(val) => onChange(val)}
          customInputProps={{ width: '100%' }}
          minDate={minEndDate}
          maxDate={maxEndDate}
          popperPlacement="right-start"
        />
      </Flex>
      <TextLabel color="text.g2" mt={1}>
        Timeframe for which you are assessing the employees' performance
      </TextLabel>
    </Box>
  );
};

const ContinuousPeriodSelector = () => {
  const { reviewPeriodDetails } = useReviewCreationV1Data();

  const { startDateOffset, endDateOffset } = reviewPeriodDetails ?? {};
  const { mutate: updateReviewPeriod } = useUpdateReviewPeriod();

  const dispatch = useDispatch();

  const onChangePropertyHandler = ({ valueAsNumber, changedPropertyKey, isBlurEvent = false }) => {
    const valueAsNumber1 = valueAsNumber === undefined ? null : valueAsNumber;

    dispatch(setReviewPeriodDetails({ [changedPropertyKey]: valueAsNumber1 }));
    if (isBlurEvent) {
      updateReviewPeriod({ changedPropertyKey, changedPropertyValue: valueAsNumber1 });
    }
  };

  return (
    <Box gridColumn="2 / span 2">
      <Flex gap={3} alignItems="center">
        Period starts from
        <NumberInput
          value={startDateOffset ?? ''}
          onBlur={(e) => {
            const valueAsNumber = e.target.value === '' ? null : Number(e.target.value);
            return onChangePropertyHandler({ valueAsNumber, changedPropertyKey: 'startDateOffset', isBlurEvent: true });
          }}
          onChange={(valueAsString, valueAsNumber) =>
            onChangePropertyHandler({ valueAsNumber, changedPropertyKey: 'startDateOffset' })
          }
          textAlign="start"
          w="60px"
          type="number"
          min={MIN_CONTINUOUS_REVIEW_START_OFFSET}
          max={MAX_CONTINUOUS_REVIEW_START_OFFSET}
          inputMode="numeric"
          format={(val) => (Number.isNaN(val) ? '' : val)}
          isInvalid={!(startDateOffset <= endDateOffset)}
          clampValueOnBlur={false}
        />
        <TextBodyMedium> days to</TextBodyMedium>
        <NumberInput
          value={endDateOffset ?? ''}
          onBlur={(e) => {
            const valueAsNumber = e.target.value === '' ? null : Number(e.target.value);
            return onChangePropertyHandler({ valueAsNumber, changedPropertyKey: 'endDateOffset', isBlurEvent: true });
          }}
          onChange={(valueAsString, valueAsNumber) =>
            onChangePropertyHandler({ valueAsNumber, changedPropertyKey: 'endDateOffset' })
          }
          textAlign="start"
          w="60px"
          type="number"
          min={startDateOffset}
          max={MAX_CONTINUOUS_REVIEW_END_OFFSET}
          step={1}
          inputMode="numeric"
          format={(val) => (Number.isNaN(val) ? '' : val)}
          isInvalid={!(startDateOffset <= endDateOffset)}
          clampValueOnBlur={false}
        />
        <TextBodyMedium>days after date of joining</TextBodyMedium>
      </Flex>
      <TextLabel color="text.g2" mt={1}>
        Timeframe for which you are assessing the employees' performance
      </TextLabel>
    </Box>
  );
};

export const TimelinesRemindersAndLaunch = () => {
  const dispatch = useDispatch();
  const { reviewType, reviewStage } = useReviewCreationV1Data();
  const isDraft = reviewStage === REVIEW_FORM_STAGE.DRAFT;

  const handlePanelOpen = () => {
    dispatch(setReviewCreationKey({ isTimelineRemindersPanelOpen: true }));
  };

  const rightComp = (
    <Button variant="outlined" StartIcon={SettingsV1} height="32px" onClick={handlePanelOpen}>
      Configure
    </Button>
  );

  // const { mutate: updateReviewType } = useUpdateReviewType();

  // const onChangeName = (e) => {
  //   dispatch(setReviewCreationKey({ reviewCycleName: e.target.value }));
  // };

  // const handleNameBlur = (e) => {
  //   updateReviewName({ reviewCycleName: e.target.value });
  // };

  const labelComp = (
    <Flex alignItems="center" justifyContent="center">
      <TextBodyMedium color="text.g2" mr={1}>
        Reference period:
      </TextBodyMedium>
      <TooltipInfoV1 title="If your review cycle includes Objectives, they will be selected based on this date range." />
    </Flex>
  );

  return (
    <SectionWrapper
      title="Timeline, reminders and launch"
      subTitle="Review workflow dates and pre-launch validations"
      rightComp={rightComp}
    >
      <RestrictAccessContainer restrictAccess={!isDraft}>
        <LabelFieldWrapper labelComp={labelComp}>
          {reviewType === REVIEW_CREATION_TYPES.DEFAULT ? <OneTimeDatesSelector /> : <ContinuousPeriodSelector />}
        </LabelFieldWrapper>
      </RestrictAccessContainer>
      {reviewType === REVIEW_CREATION_TYPES.DEFAULT ? <OneTimeTimeline /> : <ContinuousTimeline />}
    </SectionWrapper>
  );
};
